import { iconBrush } from "@kensho/icons";
import { useLogger } from "@kensho/lumberjack";
import { Button, Tooltip } from "@kensho/neo";
import { use, useCallback } from "react";

import { useKeyboardShortcut } from "../../../hooks/use-keyboard-shortcut";
import { useTranscriptContext } from "../../../providers/use-transcript-context";
import { UserContext } from "../../../providers/user-context";
import { SHORTCUTS } from "../../../shortcuts/shortcut-registration";
import { prettyShortcutKey } from "../../../shortcuts/shortcut-section";
import { TranscriptSelection } from "../../../types/types";
import { produceEditorAction } from "../../../utils/transcript-revision-utils";
import { TranscriptPermissionsContext } from "../transcript-permissions-provider";
import { useDispatchEditorAction } from "../transcript/dispatch-editor-action-provider";

interface Props {
  transcriptId: string;
  transcriptSelection: TranscriptSelection | null;
}

export function CreateNeedsReviewHighlightButton(props: Props) {
  const { transcriptSelection } = props;
  const { transcript, version } = useTranscriptContext();
  const { transcriptPermissions } = use(TranscriptPermissionsContext);
  const { dispatchEditorAction } = useDispatchEditorAction();
  const log = useLogger();
  const { user } = use(UserContext);

  const markSelectedForReview = useCallback(async () => {
    if (
      !transcriptPermissions.edit ||
      transcriptSelection?.type !== "Range" ||
      !transcriptSelection.start ||
      !transcriptSelection.end ||
      !user
    )
      return;

    const editorAction = produceEditorAction({
      action: {
        type: "add-annotation",
        annotation: {
          source: "scribe",
          knowledgeBase: "custom",
          label: "needs-review-highlight",
          type: "comment",
          createdBy: user.email,
          createdOn: new Date().toISOString(),
        },
        ranges: [
          {
            start: {
              sliceIndex: transcriptSelection.start.sliceIndex,
              tokenIndex: transcriptSelection.start.tokenIndex,
            },
            end: {
              sliceIndex: transcriptSelection.end.sliceIndex,
              tokenIndex: transcriptSelection.end.tokenIndex,
            },
          },
        ],
      },
      transcript,
      transcriptSelection: null,
      log,
    });
    if (!editorAction) return;

    dispatchEditorAction(editorAction, version);
  }, [
    transcript,
    version,
    transcriptPermissions,
    transcriptSelection,
    dispatchEditorAction,
    log,
    user,
  ]);
  const { keys } = SHORTCUTS.Edit.highlight;

  useKeyboardShortcut(keys, () => {
    markSelectedForReview();
  });

  const label = `Highlight (${keys.map((key) => prettyShortcutKey(key)).join("")})`;
  return (
    <Tooltip content={label} position="bottom">
      <Button
        disabled={
          !transcript ||
          !transcriptPermissions.edit ||
          transcriptSelection?.type !== "Range"
        }
        onClick={markSelectedForReview}
        icon={iconBrush}
        minimal
        intent="primary"
        aria-label={label}
      />
    </Tooltip>
  );
}
